import { useState, useCallback } from 'react';
import { EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { useUploadRichTextMediaMutation } from '@/store/api/mediaApi';

/**
 * Custom hook for managing rich text editor with image upload functionality
 * 
 * @param {string} initialDescription - Initial HTML content for the editor
 * @returns {Object} Hook utilities and state
 */
const useRichTextWithUpload = (initialDescription = '') => {
  const [description, setDescription] = useState(initialDescription);
  const [descriptionState, setDescriptionState] = useState(EditorState.createEmpty());
  const [contentImgs, setContentImgs] = useState({});
  const [uploadMedia] = useUploadRichTextMediaMutation();

  /**
   * Handle description content change from the editor
   */
  const handleDescriptionChange = useCallback((content) => {
    setDescription(content);
  }, []);

  /**
   * Handle editor state change (for tracking images)
   */
  const handleDescriptionStateChange = useCallback((state) => {
    setDescriptionState(state);
  }, []);

  /**
   * Handle content images change from the editor
   */
  const handleContentImgChange = useCallback((imgs) => {
    setContentImgs(imgs);
  }, []);

  /**
   * Process and upload images, then return the final HTML content
   * @returns {Promise<string>} Final HTML content with server image URLs
   */
  const processAndUploadImages = useCallback(async () => {
    let htmlContent = description;
    console.log('description', description);
    console.log('contentImgs', contentImgs);
    if (Object.keys(contentImgs).length > 0) {
      // Upload images to server
      const imageUploadPromises = Object.keys(contentImgs).map(
        async (tempId) => {
          const { file } = contentImgs[tempId];
          const formData = new FormData();
          formData.append('file', file);

          const response = await uploadMedia(formData).unwrap();
          return { tempId, serverUrl: response.url };
        }
      );

      // Wait for all images to finish uploading
      const uploadedImages = await Promise.all(imageUploadPromises);

      // Replace temporary local URLs with final server URLs in the editor content
      const contentState = descriptionState.getCurrentContent();
      const rawContentState = convertToRaw(contentState);
      const newRawContentState = { ...rawContentState };

      uploadedImages.forEach(({ tempId, serverUrl }) => {
        Object.values(newRawContentState?.entityMap || {}).forEach((entity) => {
          const dataTempId = entity.data?.src?.split('#tempId=')[1];
          if (entity.type === 'IMAGE' && dataTempId === tempId) {
            entity.data.src = serverUrl;
          }
        });
      });

      // Convert the updated content to HTML
      htmlContent = draftToHtml(newRawContentState);
    }

    return htmlContent;
  }, [description, contentImgs, descriptionState, uploadMedia]);

  /**
   * Reset all state to initial values
   */
  const resetRichText = useCallback(() => {
    setDescription('');
    setDescriptionState(EditorState.createEmpty());
    setContentImgs({});
  }, []);

  /**
   * Set values programmatically (useful for editing)
   */
  const setRichTextValues = useCallback((desc, state = EditorState.createEmpty()) => {
    setDescription(desc || '');
    setDescriptionState(state);
    setContentImgs({});
  }, []);

  /**
   * Check if there are images being uploaded
   */
  const hasImages = Object.keys(contentImgs).length > 0;

  return {
    // State values
    description,
    descriptionState,
    contentImgs,
    hasImages,

    // Handlers for RichTextEditor props
    handleDescriptionChange,
    handleDescriptionStateChange,
    handleContentImgChange,

    // Utility functions
    processAndUploadImages,
    resetRichText,
    setRichTextValues,
  };
};

export default useRichTextWithUpload;
